import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Save, Lock, KeyRound, Send } from "lucide-react";
import { Wordmark } from "../components/landing/Wordmark";
import { fetchContent } from "../lib/api";
import { loadCaptcha } from "../lib/captcha";
import { getPlannerConfig, calculatePlan, updatePlan, unlockPlan } from "../components/planner/plannerApi";
import StepProfile from "../components/planner/StepProfile";
import StepReturns from "../components/planner/StepReturns";
import StepInvestments, { computePortfolio } from "../components/planner/StepInvestments";
import StepCashFlow from "../components/planner/StepCashFlow";
import StepProjection from "../components/planner/StepProjection";
import ScenarioTabs from "../components/planner/ScenarioTabs";
import SavePlanDialog from "../components/planner/SavePlanDialog";
import { plannerTrack } from "../components/planner/analytics";

const CURRENT_YEAR = new Date().getFullYear();
const DRAFT_KEY = "oth_diy_draft_v2";
const MAX_SCENARIOS = 3;

const STEPS = [
    { id: 0, label: "About You" },
    { id: 1, label: "Assumptions" },
    { id: 2, label: "Your Investments" },
    { id: 3, label: "Cash Flow & Goals" },
    { id: 4, label: "Wealth Projection" },
];

const PROJECTION_STEP = 4;

const newScenarioId = () => `scn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// Per-scenario defaults (assumptions + cash-flow & goals).
const blankScenario = (i = 0) => ({
    id: newScenarioId(),
    name: `Scenario ${i + 1}`,
    assumptions: { inflation: 6, risk_free_rate: 6, tax_rate: 30, projection_end_year: "" },
    income: { annual_income: "", stop_year: "", growth_rate: 7 },
    income_streams: [],
    expenses: { monthly_expense: "" },
    cars: [], houses: [], dependants: [], children: [], inheritances: [], misc: [], emis: [],
    growth: { rate: "", reduced_rate: "", reduced_start_year: "", reduced_end_year: "" },
});

// Shared (profile, investments, returnOverrides) + one or more scenarios.
const blankPlan = () => ({ profile: {}, investments: [], returnOverrides: {}, scenarios: [blankScenario(0)] });

const SCENARIO_FIELDS = ["assumptions", "income", "income_streams", "expenses", "cars", "houses", "dependants", "children", "inheritances", "misc", "emis", "growth"];

const flatToScenario = (obj) => {
    const sc = { id: newScenarioId(), name: "Scenario 1" };
    SCENARIO_FIELDS.forEach((f) => { if (obj[f] !== undefined) sc[f] = obj[f]; });
    return sc;
};

const normalizeScenario = (s, i) => {
    const base = blankScenario(i);
    return {
        ...base,
        id: s.id || base.id,
        name: s.name || base.name,
        assumptions: { ...base.assumptions, ...(s.assumptions || {}) },
        income: { ...base.income, ...(s.income || {}) },
        income_streams: s.income_streams || [],
        expenses: { ...base.expenses, ...(s.expenses || {}) },
        cars: s.cars || [], houses: s.houses || [], dependants: s.dependants || [],
        children: s.children || [], inheritances: s.inheritances || [], misc: s.misc || [],
        emis: (s.emis || []).map((e, i) => ({ ...e, id: e.id || `emi_${Date.now()}_${i}`, name: e.name ?? "" })),
        growth: { ...base.growth, ...(s.growth || {}) },
    };
};

const normalizePlanShape = (obj) => {
    if (!obj || typeof obj !== "object") return blankPlan();
    const shared = { profile: obj.profile || {}, investments: obj.investments || [], returnOverrides: obj.returnOverrides || {} };
    if (Array.isArray(obj.scenarios) && obj.scenarios.length) {
        return { ...shared, scenarios: obj.scenarios.map((s, i) => normalizeScenario(s, i)) };
    }
    return { ...shared, scenarios: [normalizeScenario(flatToScenario(obj), 0)] };
};

const loadInitialPlan = () => {
    try {
        localStorage.removeItem("oth_diy_draft_v1");
        sessionStorage.removeItem("oth_diy_draft_v1");
        const raw = sessionStorage.getItem(DRAFT_KEY);
        if (raw) return normalizePlanShape(JSON.parse(raw));
    } catch (e) { console.debug("draft parse failed", e); }
    return blankPlan();
};

const inputCls = "w-full bg-white border border-oth-line rounded-xl px-3.5 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors";

const DisclaimerNote = ({ onOpenFull }) => (
    <div className="mt-10 rounded-2xl border border-oth-line/60 bg-[#f0f0ec] px-5 py-4 text-[12px] leading-relaxed font-body text-oth-ink3" data-testid="planner-disclaimer">
        <strong className="text-oth-ink2">Not advice.</strong> Nothing on this page should be considered investment, financial, tax or legal advice of any form.
        This is a do-it-yourself planning tool to be used entirely at your own discretion — all projections are illustrative and depend on the assumptions you enter.
        Please consult a qualified adviser before acting on anything here.
        {onOpenFull && (
            <>
                {" "}
                <button type="button" onClick={onOpenFull} className="underline text-oth-blue hover:text-oth-ink transition-colors" data-testid="planner-full-disclaimer-link">
                    Read our full disclaimer →
                </button>
            </>
        )}
    </div>
);

const SiteDisclaimerModal = ({ disclaimer, onClose }) => (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="site-disclaimer-modal">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-2xl rounded-3xl bg-white p-7 md:p-9 shadow-2xl max-h-[85vh] overflow-y-auto thin-scroll">
            <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-full border border-oth-line/70 flex items-center justify-center text-oth-ink3 hover:text-oth-ink" data-testid="site-disclaimer-close" aria-label="Close">✕</button>
            <div className="text-[11px] uppercase tracking-[0.25em] text-oth-blue font-body mb-2">{disclaimer?.eyebrow || "Legal"}</div>
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">{disclaimer?.title || "Disclaimer"}</h2>
            <div className="font-body text-sm text-oth-ink2 leading-relaxed whitespace-pre-wrap" data-testid="site-disclaimer-body">
                {(typeof disclaimer?.body === "string" && disclaimer.body.trim()) ? disclaimer.body : "Please refer to the disclaimer on our main website."}
            </div>
        </div>
    </div>
);

function UnlockGate({ code, onUnlocked }) {
    const [secret, setSecret] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const go = async (e) => {
        e?.preventDefault();
        setBusy(true); setErr("");
        try {
            const res = await unlockPlan(code, secret);
            onUnlocked(res, secret);
        } catch (ex) {
            setErr(ex?.response?.data?.detail || "Could not unlock the plan.");
        } finally { setBusy(false); }
    };
    return (
        <div className="min-h-[70vh] flex items-center justify-center px-4">
            <form onSubmit={go} className="w-full max-w-md rounded-3xl border border-oth-line/70 bg-white p-8 md:p-10" data-testid="unlock-gate">
                <div className="w-12 h-12 rounded-2xl bg-[#eef3f8] flex items-center justify-center text-oth-blue mb-5"><KeyRound size={20} /></div>
                <h1 className="font-display text-2xl font-semibold tracking-tight">Unlock your plan</h1>
                <p className="text-sm text-oth-ink3 font-body mt-2">Plan <span className="font-semibold text-oth-ink">{code}</span> is encrypted. Enter the secret you set when saving it.</p>
                <input type="password" autoFocus className={`${inputCls} mt-5`} placeholder="Your secret" value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="unlock-secret-input" />
                {err && <div className="text-sm text-red-600 font-body mt-3" data-testid="unlock-error">{err}</div>}
                <button type="submit" disabled={busy || !secret} className="btn-pill btn-pill-blue w-full justify-center text-sm mt-5 disabled:opacity-50" data-testid="unlock-submit">
                    {busy ? "Unlocking…" : "Unlock plan"}
                </button>
                <p className="text-xs text-oth-ink3 font-body mt-4 leading-relaxed">There is no recovery if the secret is lost — One Tree Hill cannot access or reset it. <Link to="/diy" className="text-oth-blue underline">Start a fresh plan</Link> instead.</p>
            </form>
        </div>
    );
}

export default function DiyPlanner() {
    const { code: routeCode } = useParams();
    const [config, setConfig] = useState(null);
    const [brand, setBrand] = useState(null);
    const [siteDisclaimer, setSiteDisclaimer] = useState(null);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [plan, setPlan] = useState(loadInitialPlan);
    const [activeScenarioId, setActiveScenarioId] = useState(() => plan.scenarios[0].id);
    const [step, setStep] = useState(0);
    const [locked, setLocked] = useState(!!routeCode);
    const [code, setCode] = useState(routeCode || null);
    const [secret, setSecret] = useState("");
    const [scenarioProjs, setScenarioProjs] = useState(null);
    const [projErrors, setProjErrors] = useState(null);
    const [saveOpen, setSaveOpen] = useState(false);
    const [updating, setUpdating] = useState(false);

    const activeScenario = plan.scenarios.find((s) => s.id === activeScenarioId) || plan.scenarios[0];

    useEffect(() => { getPlannerConfig().then(setConfig).catch(() => toast.error("Could not load planner configuration.")); }, []);
    useEffect(() => { plannerTrack("planner_visit"); }, []);
    useEffect(() => { plannerTrack("step_reached", { step: step + 1 }); }, [step]);
    useEffect(() => {
        if (plan.investments.some((r) => Number(r.amount) > 0)) plannerTrack("investments_entered");
        const agg = { cars: 0, house: 0, dependants: 0, children: 0, inheritance: 0, emis: 0, misc: 0, reduced_growth: 0 };
        plan.scenarios.forEach((sc) => {
            agg.cars += sc.cars.length; agg.house += sc.houses.length; agg.dependants += sc.dependants.length;
            agg.children += sc.children.length; agg.inheritance += sc.inheritances.length;
            agg.emis += sc.emis.length; agg.misc += sc.misc.length;
            if (sc.growth.reduced_rate !== "" && sc.growth.reduced_rate !== null) agg.reduced_growth += 1;
        });
        Object.entries(agg).forEach(([section, n]) => { if (n > 0) plannerTrack("section_used", { section }); });
    }, [plan]);
    useEffect(() => {
        fetchContent().then((c) => {
            setBrand(c?.brand || null);
            setSiteDisclaimer(c?.footer?.disclaimer || null);
            loadCaptcha(c?.integrations?.recaptcha_site_key);
        }).catch((e) => console.debug("brand load failed", e));
    }, []);
    useEffect(() => {
        if (!routeCode) {
            try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(plan)); } catch (e) { console.debug("draft save failed", e); }
        }
    }, [plan, routeCode]);

    const { total: startingWealth, weighted } = useMemo(() => computePortfolio(plan.investments), [plan.investments]);

    const buildPayload = useCallback(() => {
        const cleanScenario = (sc) => ({
            id: sc.id,
            name: (sc.name || "").trim() || "Scenario",
            assumptions: sc.assumptions,
            income: sc.income,
            income_streams: (sc.income_streams || []).filter((s) => s.start_year && s.amount !== ""),
            expenses: sc.expenses,
            cars: (sc.cars || []).filter((c) => c.year && c.current_value !== ""),
            houses: (sc.houses || []).filter((h) => h.year && h.current_value !== ""),
            dependants: (sc.dependants || []).filter((d) => d.start_year && d.annual_amount !== ""),
            children: (sc.children || []).map((c) => ({
                id: c.id, name: c.name,
                education: c.education?.start_year ? c.education : null,
                higher_education: c.higher_education?.start_year ? c.higher_education : null,
                marriage: c.marriage?.year ? c.marriage : null,
            })).filter((c) => c.education || c.higher_education || c.marriage),
            inheritances: (sc.inheritances || []).filter((x) => x.year && x.amount !== ""),
            misc: (sc.misc || []).filter((m) => m.year && m.amount !== ""),
            emis: (sc.emis || []).map((e, i) => ({ ...e, id: e.id || `emi_${i}`, name: (e.name || "").trim() || `EMI ${i + 1}` })).filter((e) => {
                const mode = e.mode || (e.loan_amount !== undefined && e.loan_amount !== "" ? "loan" : (e.annual_amount ? "simple" : "loan"));
                if (mode === "loan") return !!(e.start_year && (e.loan_amount !== "" || e.downpayment !== ""));
                return !!(e.start_year && e.end_year && e.annual_amount !== "");
            }),
            growth: { ...sc.growth, rate: sc.growth.rate === "" ? Number(weighted.toFixed(2)) : sc.growth.rate },
        });
        return {
            current_year: CURRENT_YEAR,
            profile: (config?.questions || []).filter((q) => plan.profile[q.id] !== undefined && plan.profile[q.id] !== "")
                .map((q) => {
                    let value = plan.profile[q.id];
                    if (q.type === "children" && value && typeof value === "object") {
                        const ages = (value.ages || []).filter((a) => a !== "").join(", ");
                        value = !value.count ? "No children" : `${value.count} ${value.count === 1 ? "child" : "children"}${ages ? ` — ages ${ages}` : ""}`;
                    }
                    return { question_id: q.id, label: q.label, value, maps_to: q.maps_to || "" };
                }),
            investments: plan.investments.map((r) => ({
                category_id: r.category_id, category_name: r.category_name,
                subcategory_id: r.subcategory_id, subcategory_name: r.subcategory_name,
                amount: Number(r.amount) || 0, expected_return: Number(r.expected_return) || 0,
                is_custom: !!r.is_custom,
            })),
            scenarios: plan.scenarios.map(cleanScenario),
        };
    }, [plan, config, weighted]);

    const contact = useMemo(() => {
        const out = { name: "", email: "", phone: "" };
        (config?.questions || []).forEach((q) => {
            if (q.maps_to && plan.profile[q.id]) out[q.maps_to] = String(plan.profile[q.id]);
        });
        return out;
    }, [config, plan.profile]);

    const runProjection = useCallback(async () => {
        setScenarioProjs(null); setProjErrors(null);
        try {
            const res = await calculatePlan(buildPayload());
            setScenarioProjs(res.scenarios || []);
            plannerTrack("projection_viewed");
        } catch (e) {
            const d = e?.response?.data?.detail;
            setProjErrors(Array.isArray(d?.errors) ? d.errors : ["Could not calculate the projection. Please review your inputs."]);
        }
    }, [buildPayload]);

    useEffect(() => { if (step === 3 || step === 4) runProjection(); }, [step, runProjection]);

    const clientChecks = () => {
        const errs = [];
        const required = (config?.questions || []).filter((q) => q.required && !plan.profile[q.id]);
        if (required.length) errs.push(`Please answer: ${required.map((q) => `"${q.label}"`).join(", ")} in About You.`);
        if (plan.investments.length === 0 || startingWealth <= 0) errs.push("Add at least one investment with an amount in Your Investments.");
        return errs;
    };

    const gotoStep = (n) => {
        if (n === 3) {
            const errs = clientChecks();
            if (errs.length) { errs.forEach((e) => toast.error(e)); return; }
        }
        setStep(n);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // ---- scenario CRUD
    const updateScenario = (id, updater) => setPlan((prev) => ({
        ...prev,
        scenarios: prev.scenarios.map((s) => (s.id === id ? updater(s) : s)),
    }));
    const renameScenario = (id, name) => updateScenario(id, (s) => ({ ...s, name }));
    const duplicateScenario = () => {
        if (plan.scenarios.length >= MAX_SCENARIOS) { toast.error(`Maximum of ${MAX_SCENARIOS} scenarios.`); return; }
        const src = plan.scenarios.find((s) => s.id === activeScenarioId) || plan.scenarios[0];
        const copy = JSON.parse(JSON.stringify(src));
        copy.id = newScenarioId();
        copy.name = `Scenario ${plan.scenarios.length + 1}`;
        setPlan((prev) => ({ ...prev, scenarios: [...prev.scenarios, copy] }));
        setActiveScenarioId(copy.id);
        toast.success(`Cloned into "${copy.name}". Edit it independently.`);
    };
    const deleteScenario = (id) => {
        if (plan.scenarios.length <= 1) return;
        const remaining = plan.scenarios.filter((s) => s.id !== id);
        setPlan((prev) => ({ ...prev, scenarios: prev.scenarios.filter((s) => s.id !== id) }));
        if (activeScenarioId === id) setActiveScenarioId(remaining[0]?.id);
    };

    const onUnlocked = (res, usedSecret) => {
        const payload = res.payload;
        const profileMap = {};
        (payload.profile || []).forEach((a) => { profileMap[a.question_id] = a.value; });
        const adminMap = {};
        (config?.categories || []).forEach((cat) => {
            (cat.subcategories || []).forEach((sub) => { adminMap[sub.id] = Number(sub.expected_return); });
        });
        const rehydratedOverrides = {};
        (payload.investments || []).forEach((r) => {
            if (r.is_custom) return;
            const def = adminMap[r.subcategory_id];
            const cur = Number(r.expected_return);
            if (def !== undefined && !Number.isNaN(cur) && Math.abs(def - cur) > 1e-6) rehydratedOverrides[r.subcategory_id] = cur;
        });
        let scenarios;
        if (Array.isArray(payload.scenarios) && payload.scenarios.length) {
            scenarios = payload.scenarios.map((s, i) => normalizeScenario(s, i));
        } else {
            scenarios = [normalizeScenario(flatToScenario(payload), 0)];
        }
        setPlan({ profile: profileMap, investments: payload.investments || [], returnOverrides: rehydratedOverrides, scenarios });
        setActiveScenarioId(scenarios[0].id);
        setSecret(usedSecret);
        setLocked(false);
        if (res.scenarios) setScenarioProjs(res.scenarios);
        else if (res.projection) setScenarioProjs([{ id: scenarios[0].id, name: scenarios[0].name, projection: res.projection }]);
        setStep(PROJECTION_STEP);
        plannerTrack("plan_unlocked");
        toast.success("Plan unlocked. You can edit any step and save changes.");
    };

    const saveChanges = async () => {
        setUpdating(true);
        try {
            await updatePlan(code, secret, buildPayload());
            toast.success("Changes saved to your encrypted plan.");
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(Array.isArray(d?.errors) ? d.errors[0] : (typeof d === "string" ? d : "Could not save changes."));
        } finally { setUpdating(false); }
    };

    if (!config) return <div className="min-h-screen flex items-center justify-center font-editorial italic text-oth-ink3" data-testid="planner-loading">Loading planner…</div>;

    return (
        <div className="min-h-screen" data-testid="diy-planner-page">
            <div className="noise-layer" aria-hidden />
            <header className="sticky top-0 z-40 bg-[rgba(245,245,242,0.9)] backdrop-blur-md border-b border-oth-line/70">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                        <Link to="/" data-testid="planner-home-link">
                            <Wordmark
                                logoUrl={brand?.logo_url || ""}
                                brandName={brand?.name || "One Tree Hill"}
                                wordmark={brand?.wordmark || "Prime Wealth"}
                                logoSize={brand?.logo_size || "md"}
                                nameColor={brand?.name_color || ""}
                                nameFont={brand?.name_font || ""}
                                nameSize={brand?.name_size || 0}
                            />
                        </Link>
                    </div>
                    {!locked && (
                        code && secret ? (
                            <div className="flex items-center gap-2">
                                <button onClick={saveChanges} disabled={updating} className="btn-pill btn-pill-light text-sm disabled:opacity-50" data-testid="save-changes-btn"><Save size={14} /> {updating ? "Saving…" : "Save changes"}</button>
                                <button onClick={() => setSaveOpen(true)} className="btn-pill btn-pill-blue text-sm" data-testid="header-share-btn"><Send size={14} /> Share with OTH</button>
                            </div>
                        ) : (
                            <button onClick={() => { const errs = clientChecks(); if (errs.length) { errs.forEach((e) => toast.error(e)); return; } setSaveOpen(true); }} className="btn-pill btn-pill-blue text-sm" data-testid="open-save-dialog-btn"><Lock size={14} /> Save plan</button>
                        )
                    )}
                </div>
            </header>

            {locked ? (
                <div>
                    <UnlockGate code={routeCode} onUnlocked={onUnlocked} />
                    <div className="max-w-md mx-auto px-4 pb-10"><DisclaimerNote onOpenFull={() => setShowDisclaimer(true)} /></div>
                </div>
            ) : (
                <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
                    <div className="mb-8">
                        <div className="text-[11px] uppercase tracking-[0.25em] text-oth-blue font-body mb-2">One Tree Hill Prime Wealth</div>
                        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Plan your wealth, privately.</h1>
                        <p className="text-sm md:text-base text-oth-ink3 font-body mt-2 max-w-2xl">Map your investments, cash flows and life goals into a year-by-year wealth projection. Build up to three what-if scenarios and compare them side by side. Encrypted with your own secret — we can't see it unless you share it.</p>
                    </div>

                    <div className="flex items-center gap-1.5 md:gap-2 mb-8 overflow-x-auto thin-scroll pb-1" data-testid="step-nav">
                        {STEPS.map((s, i) => (
                            <React.Fragment key={s.id}>
                                {i > 0 && <div className="w-5 md:w-8 h-px bg-oth-line shrink-0" />}
                                <button
                                    onClick={() => gotoStep(s.id)}
                                    className={`shrink-0 inline-flex items-center gap-2 px-3.5 md:px-4 py-2 rounded-full border text-[13px] font-body transition-colors ${step === s.id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-oth-line text-oth-ink2 hover:border-oth-blue"}`}
                                    data-testid={`step-tab-${s.id}`}
                                >
                                    <span className={`w-5 h-5 rounded-full text-[11px] flex items-center justify-center ${step === s.id ? "bg-white text-oth-ink" : "bg-[#f0f0ec] text-oth-ink3"}`}>{i + 1}</span>
                                    {s.label}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>

                    {(step === 3 || step === 4) && (
                        <ScenarioTabs
                            scenarios={plan.scenarios}
                            activeId={activeScenarioId}
                            onSelect={setActiveScenarioId}
                            onRename={renameScenario}
                            onDuplicate={duplicateScenario}
                            onDelete={deleteScenario}
                            max={MAX_SCENARIOS}
                        />
                    )}

                    {step === 0 && <StepProfile questions={config.questions} profile={plan.profile} setProfile={(v) => setPlan({ ...plan, profile: v })} />}
                    {step === 1 && (
                        <StepReturns
                            categories={config.categories}
                            returnOverrides={plan.returnOverrides || {}}
                            setReturnOverrides={(v) => setPlan((prev) => ({ ...prev, returnOverrides: v }))}
                            investments={plan.investments}
                            setInvestments={(v) => setPlan((prev) => ({ ...prev, investments: v }))}
                        />
                    )}
                    {step === 2 && (
                        <StepInvestments
                            categories={config.categories}
                            investments={plan.investments}
                            setInvestments={(v) => setPlan({ ...plan, investments: v })}
                            returnOverrides={plan.returnOverrides || {}}
                        />
                    )}
                    {step === 3 && (
                        <StepCashFlow
                            key={activeScenario.id}
                            plan={activeScenario}
                            setPlan={(ns) => updateScenario(activeScenario.id, () => ns)}
                            startingWealth={startingWealth}
                            weightedReturn={weighted}
                            currentYear={CURRENT_YEAR}
                        />
                    )}
                    {step === 4 && (
                        <StepProjection
                            scenarios={scenarioProjs}
                            errors={projErrors}
                            buildPayload={buildPayload}
                            planCode={code}
                            planSaved={!!code}
                            activeId={activeScenarioId}
                        />
                    )}

                    <div className="flex items-center justify-between mt-10">
                        <button onClick={() => gotoStep(Math.max(0, step - 1))} disabled={step === 0} className="btn-pill btn-pill-light text-sm disabled:opacity-40" data-testid="step-back-btn"><ArrowLeft size={14} /> Back</button>
                        {step < PROJECTION_STEP ? (
                            <button onClick={() => gotoStep(step + 1)} className="btn-pill btn-pill-dark text-sm" data-testid="step-next-btn">
                                {step === PROJECTION_STEP - 1 ? "See my wealth projection" : "Continue"} <ArrowRight size={14} />
                            </button>
                        ) : (
                            !code && (
                                <button onClick={() => setSaveOpen(true)} className="btn-pill btn-pill-blue text-sm" data-testid="projection-save-btn"><Lock size={14} /> Save my plan</button>
                            )
                        )}
                    </div>
                    <DisclaimerNote onOpenFull={() => setShowDisclaimer(true)} />
                </main>
            )}

            {showDisclaimer && <SiteDisclaimerModal disclaimer={siteDisclaimer} onClose={() => setShowDisclaimer(false)} />}

            <SavePlanDialog
                open={saveOpen}
                onClose={() => setSaveOpen(false)}
                buildPayload={buildPayload}
                contact={contact}
                code={code}
                secret={secret}
                onSaved={(newCode, usedSecret) => { setCode(newCode); setSecret(usedSecret); plannerTrack("plan_saved"); try { sessionStorage.removeItem(DRAFT_KEY); } catch (e) { console.debug(e); } }}
            />
        </div>
    );
}
